import { Controller } from "@hotwired/stimulus"
import { debounce } from "lodash"
import { escapeHtml } from "../../lib/chat_utils"

export default class extends Controller {
  static targets = ["modal", "input", "loading", "results"]

  connect() {
    this.debouncedSearch = debounce(this.performSearch.bind(this), 300)
  }

  disconnect() {
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel()
    }
  }

  open() {
    document.dispatchEvent(new CustomEvent('chat:close-metadata'))
    document.dispatchEvent(new CustomEvent('chat:close-message-search'))

    this.modalTarget.classList.remove('hidden')
    this.clearResults()

    if (this.hasInputTarget) {
      this.inputTarget.value = ''
      setTimeout(() => this.inputTarget.focus(), 50)
    }
  }

  close() {
    this.modalTarget.classList.add('hidden')
    this.clearResults()
  }

  handleInput(event) {
    const query = (event.target.value || '').trim()

    if (query.length < 2) {
      this.hideLoading()
      this.renderHint(query)
      return
    }

    this.showLoading()
    this.debouncedSearch(query)
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    }
  }

  async performSearch(query) {
    try {
      const response = await fetch(`/admin/chats/user_search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('User search failed')

      const users = await response.json()
      this.hideLoading()
      this.renderResults(users, query)
    } catch (error) {
      console.error('User search error:', error)
      this.hideLoading()
      this.renderError()
    }
  }

  selectUser(event) {
    const userId = event.currentTarget.dataset.userId
    if (!userId) return

    this.close()

    document.dispatchEvent(new CustomEvent('chat:select-conversation', {
      detail: { partnerType: 'user', partnerId: Number(userId) }
    }))
  }

  clearResults() {
    this.hideLoading()
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ''
    }
  }

  showLoading() {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.classList.remove('hidden')
  }

  hideLoading() {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.classList.add('hidden')
  }

  renderHint(query) {
    if (!this.hasResultsTarget) return

    if (query.length === 0) {
      this.resultsTarget.innerHTML = '<div class="text-xs text-slate-400">Type at least 2 characters to search.</div>'
      return
    }

    this.resultsTarget.innerHTML = '<div class="text-xs text-slate-400">Keep typing\u2026</div>'
  }

  renderError() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = '<div class="text-xs text-red-300">Failed to search users. Please try again.</div>'
  }

  renderResults(users, query) {
    if (!this.hasResultsTarget) return

    if (!users || users.length === 0) {
      this.resultsTarget.innerHTML = `<div class="text-xs text-slate-400">No users found for "${escapeHtml(query)}"</div>`
      return
    }

    const items = users.map((u) => {
      const name = escapeHtml(u.name || '')
      const email = escapeHtml(u.email || '')

      return `
        <button type="button"
                class="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-800 border border-slate-700 bg-slate-900 text-slate-100 transition mb-2"
                data-user-id="${u.id}"
                data-action="click->admin--chat-user-search#selectUser">
          <div class="flex items-center justify-between gap-2">
            <div class="min-w-0">
              <div class="text-sm font-medium truncate">${name}</div>
              <div class="text-xs text-slate-400 truncate">${email}</div>
            </div>
            <iconify-icon class="text-slate-300" icon="mdi-arrow-right"></iconify-icon>
          </div>
        </button>
      `
    }).join('')

    this.resultsTarget.innerHTML = items
  }
}
